import { useEffect, useMemo, useState } from "react";

import { Observation } from "@medplum/fhirtypes";
import { useMedplum } from "@medplum/react";

import { Group, List, Table } from "@mantine/core";

import { TextWrapper } from "@/components/TextWrapper";
import ActionCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/ActionCell";
import LastUpdated from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/LastUpdated";
import PreviousVersions from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions";
import classes from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/PreviousVersions.module.css";
import TimeCell from "@/encounters/components/EncounterLabor/EncounterLaborTable/LaborRow/Common/TimeCell";
import { hasPreviousVersions } from "@/encounters/components/EncounterLabor/services/labor";
import { getTransportRowValues } from "@/encounters/components/EncounterLabor/utils/labor";

interface TransportRowProps {
  time: string;
  observation: Observation;
  showNumbersOnly: boolean;
  showPreviousVersions?: boolean;
  isViewOnly?: boolean;
}

interface TransportItem {
  label: string;
  value: string | string[] | undefined;
  showOther?: boolean;
  otherValue?: string | undefined;
}

const INITIAL_TRANSPORT_VALUES = {
  type: undefined,
  decidingProvider: undefined,
  contact: undefined,
  reason: undefined,
  newbornDisposition: undefined,
  newbornDispositionOther: undefined,
  clientDisposition: undefined,
  clientDispositionOther: undefined,
  transportingFrom: undefined,
  hospitalName: undefined,
  hospitalContact: undefined,
  condition: undefined,
  firstCall: undefined,
  emsArrived: undefined,
  timeLeft: undefined,
  arrivedAtHospital: undefined,
  notes: undefined,
  hasAnswers: false,
  hasNotes: false,
} as const;

export default function TransportRow({
  time,
  observation,
  showNumbersOnly,
  showPreviousVersions = true,
  isViewOnly = false,
}: TransportRowProps) {
  const medplum = useMedplum();
  const [transportValues, setTransportValues] =
    useState<Awaited<ReturnType<typeof getTransportRowValues>>>(INITIAL_TRANSPORT_VALUES);

  useEffect(() => {
    getTransportRowValues(observation, medplum).then(setTransportValues);
  }, [observation, medplum]);

  const { hasAnswers, hasNotes, notes, ...values } = transportValues;

  const transportItems = useMemo<TransportItem[]>(
    () => [
      { label: "Type", value: values.type },
      { label: "Reason", value: values.reason },
      { label: "Deciding Provider", value: values.decidingProvider },
      { label: "Contact", value: values.contact },
      {
        label: "Newborn Disposition",
        value: values.newbornDisposition,
        showOther: true,
        otherValue: values.newbornDispositionOther,
      },
      {
        label: "Client Disposition",
        value: values.clientDisposition,
        showOther: true,
        otherValue: values.clientDispositionOther,
      },
      { label: "Transporting From", value: values.transportingFrom },
      { label: "Hospital Name", value: values.hospitalName },
      { label: "Hospital Contact", value: values.hospitalContact },
      { label: "Condition", value: values.condition },
      { label: "First Call", value: values.firstCall },
      { label: "EMS Arrived", value: values.emsArrived },
      { label: "Time Left", value: values.timeLeft },
      { label: "Arrived At Hospital", value: values.arrivedAtHospital },
    ],
    [values],
  );

  const previousVersions = hasPreviousVersions(observation);

  if ((!hasAnswers && !hasNotes) || showNumbersOnly) return null;

  return (
    <Table.Tr bg="white" className={classes.tableRow}>
      {showPreviousVersions && (
        <TimeCell time={time} rowSpan={1} isViewOnly={isViewOnly} observation={observation} />
      )}
      <Table.Td p="md" colSpan={10}>
        {hasAnswers && (
          <>
            <TextWrapper size="sm">Transport:</TextWrapper>
            <List fz="sm" withPadding>
              {transportItems.map((item, index) => {
                const hasValue = item.value || (item.showOther && item.otherValue);
                if (!hasValue) return null;

                // Handle array values (like reasons and dispositions)
                if (Array.isArray(item.value)) {
                  return (
                    <List.Item key={`${item.label}-${index}`}>
                      <TextWrapper size="sm" variant="bold">
                        {item.label}:
                      </TextWrapper>
                      <List ml="md" size="sm" spacing="xs">
                        {item.value.map((value, valueIndex) => (
                          <List.Item key={`${item.label}-${valueIndex}`}>
                            <TextWrapper size="sm">{value}</TextWrapper>
                          </List.Item>
                        ))}
                      </List>
                    </List.Item>
                  );
                }

                // Handle single values
                return (
                  <List.Item key={`${item.label}-${item.value || item.otherValue}`}>
                    <Group gap="xs">
                      <TextWrapper size="sm" variant="bold">
                        {item.label}:
                      </TextWrapper>
                      <TextWrapper size="sm">{item.value || item.otherValue}</TextWrapper>
                    </Group>
                  </List.Item>
                );
              })}
            </List>
          </>
        )}
        {hasNotes && (
          <TextWrapper pt="xs" size="sm">
            Transport Comments: {notes}
          </TextWrapper>
        )}
        {showPreviousVersions && previousVersions ? (
          <PreviousVersions
            observation={observation}
            rowComponent={TransportRow}
            showPreviousVersions={showPreviousVersions}
          />
        ) : (
          <LastUpdated observation={observation} showPreviousVersions={false} />
        )}
      </Table.Td>
      {showPreviousVersions && !isViewOnly && <ActionCell rowSpan={1} observation={observation} />}
    </Table.Tr>
  );
}
